<template>
  <v-container id="dashboard" fluid tag="section" class="dashboard-wrapper">
    <section class="welcome-section">
      <div class="welcome-bg"></div>

      <div class="welcome-content">
        <div class="welcome-left">
          <div class="welcome-badge">
            <span class="pulse-dot"></span>
            Sistem Online
          </div>

          <h1>
            Selamat datang,
            <strong>{{ adminName }}</strong>
          </h1>

          <p>Pantau dan kelola data Bondowoso Berkah dari satu dashboard.</p>

          <div class="welcome-meta">
            <div class="meta-item">
              <v-icon small>mdi-calendar-blank-outline</v-icon>
              {{ currentDate }}
            </div>
          </div>
        </div>

        <div class="welcome-visual">
          <img src="/images/gambardashboard.png" alt="" />
        </div>
      </div>
    </section>

    <section class="overview-section">
      <div class="section-heading">
        <div>
          <span class="eyebrow">OVERVIEW</span>
          <h2>Ringkasan Data</h2>
        </div>

        <span class="last-update">
          <span class="update-dot"></span>
          Data terkini
        </span>
      </div>

      <div class="overview-grid">
        <div v-for="(stat, i) in statCards" :key="i" class="overview-item">
          <div class="overview-icon" :style="{ background: stat.bg }">
            <v-icon :color="stat.color" size="22">
              {{ stat.icon }}
            </v-icon>
          </div>

          <div class="overview-content">
            <div class="overview-label">
              {{ stat.title }}
            </div>

            <div class="overview-value">
              <v-progress-circular
                v-if="loading"
                indeterminate
                size="18"
                width="2"
                :color="stat.color"
              />
              <template v-else>
                {{ stat.value }}
              </template>
            </div>
          </div>

          <div class="overview-arrow">
            <v-icon size="18">mdi-arrow-top-right</v-icon>
          </div>
        </div>
      </div>
    </section>

    <section class="content-section">
      <div class="main-panel map-panel">
        <div class="panel-header">
          <div>
            <span class="eyebrow">GEOSPASIAL</span>
            <h3>Sebaran Potensi Desa</h3>
            <p>Distribusi potensi berdasarkan wilayah desa</p>
          </div>

          <v-btn text small class="panel-action" to="/pages/potensidesa">
            Lihat detail
            <v-icon right small> mdi-arrow-top-right </v-icon>
          </v-btn>
        </div>

        <div class="map-container">
          <div id="dashboard-map"></div>

          <div v-if="mapLoading" class="map-loading">
            <v-progress-circular indeterminate color="#018C74" size="32" width="3" />
            <span>Memuat peta...</span>
          </div>

          <div class="map-floating-info">
            <div class="map-info-title">Potensi Terdata</div>

            <div class="map-info-value">
              {{ formatNumber(potensiSpasial.length) }}
            </div>

            <div class="map-info-sub">titik potensi</div>
          </div>

          <div v-if="legendBuckets.length" class="map-legend">
            <div class="legend-title">Intensitas potensi</div>

            <div v-for="b in legendBuckets" :key="b.label" class="legend-item">
              <span class="legend-color" :style="{ background: b.color }"></span>

              <span>{{ b.label }}</span>
            </div>
          </div>
        </div>

        <div class="map-footer">
          <div class="map-footer-item">
            <v-icon small>mdi-map-marker-multiple-outline</v-icon>
            {{ formatNumber(potensiSpasial.length) }} titik
          </div>

          <div class="map-footer-item">
            <v-icon small>mdi-home-city-outline</v-icon>
            219 desa / kelurahan
          </div>

          <v-btn depressed small class="map-detail-btn" to="/pages/potensidesa">
            Eksplorasi Peta
            <v-icon right small> mdi-arrow-right </v-icon>
          </v-btn>
        </div>
      </div>

      <div class="main-panel category-panel">
        <div class="panel-header">
          <div>
            <span class="eyebrow">INSIGHT</span>
            <h3>Kategori Potensi</h3>
            <p>Komposisi potensi yang terdata</p>
          </div>
        </div>

        <div v-if="loading" class="panel-loading">
          <v-progress-circular indeterminate color="#018C74" />
        </div>

        <div v-else-if="!kategoriPotensi.length" class="empty-state">
          <v-icon size="42"> mdi-chart-donut </v-icon>

          <span> Belum ada data potensi. </span>
        </div>

        <template v-else>
          <div class="category-summary">
            <div class="category-donut" :style="donutStyle">
              <div class="category-donut-inner">
                <strong>{{ totalPotensi }}</strong>
                <span>Total</span>
              </div>
            </div>

            <div class="category-summary-text">
              <strong>
                {{ kategoriPotensi.length }}
              </strong>

              <span> kategori potensi </span>

              <small> tersebar di wilayah Bondowoso </small>
            </div>
          </div>

          <div class="category-list">
            <div v-for="(cat, i) in kategoriPotensi" :key="i" class="category-row">
              <div class="category-name">
                <span class="category-dot" :style="{ background: cat.color }"></span>

                <span>{{ cat.label }}</span>
              </div>

              <div class="category-number">
                {{ formatNumber(cat.value) }}
              </div>

              <div class="category-percent">{{ cat.percent }}%</div>
            </div>
          </div>
        </template>
      </div>

      <div class="main-panel activity-panel">
        <div class="panel-header">
          <div>
            <span class="eyebrow">MONITORING</span>
            <h3>Aktivitas Terbaru</h3>
            <p>Perubahan data terakhir</p>
          </div>

          <div class="activity-live">
            <span></span>
            Live
          </div>
        </div>

        <div v-if="loading" class="panel-loading">
          <v-progress-circular indeterminate color="#018C74" />
        </div>

        <div v-else-if="!activities.length" class="empty-state">
          <v-icon size="42"> mdi-history </v-icon>

          <span> Belum ada aktivitas. </span>
        </div>

        <div v-else class="activity-list">
          <div v-for="(act, i) in activities" :key="i" class="activity-row">
            <div class="activity-avatar" :style="{ background: act.bg }">
              <v-icon size="17" :color="act.color">
                {{ act.icon }}
              </v-icon>
            </div>

            <div class="activity-content">
              <div class="activity-title">
                {{ act.title }}
              </div>

              <div class="activity-sub">
                {{ act.sub }}
              </div>
            </div>

            <div class="activity-time">
              {{ act.time || "—" }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="regional-section">
      <div class="regional-header">
        <div>
          <span class="eyebrow">WILAYAH</span>
          <h2>Ekosistem Bondowoso</h2>
        </div>

        <span class="regional-description">
          Data administrasi dan pengguna platform
        </span>
      </div>

      <div class="regional-grid">
        <div v-for="(q, i) in quickStats" :key="i" class="regional-item">
          <div class="regional-icon" :style="{ background: q.bg }">
            <v-icon size="21" :color="q.color">
              {{ q.icon }}
            </v-icon>
          </div>

          <div class="regional-data">
            <span>{{ q.label }}</span>
            <strong>{{ q.value }}</strong>
          </div>

          <v-icon class="regional-arrow" size="17"> mdi-arrow-top-right </v-icon>
        </div>
      </div>
    </section>
  </v-container>
</template>

<script>
import axios from "axios";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const BASE_URL = "https://berkah.bondowosokab.go.id:5500";

const API_KEY = "7039338ec2fa8ac649760ddd8404b711b207479377c846d41d5d24b7b21beb97";

const headers = {
  "Bondowoso-Berkah": API_KEY,
};

const KATEGORI_PALETTE = [
  "#018C74",
  "#2FA98F",
  "#2F80C4",
  "#7FE0C9",
  "#8FA6A3",
  "#D7DEDC",
  "#B7C4C1",
];

const ACTIVITY_PALETTE = [
  {
    color: "#018C74",
    bg: "#E6F5F1",
  },
  {
    color: "#2F80C4",
    bg: "#E8F2FB",
  },
  {
    color: "#8E4FD1",
    bg: "#F2EAFB",
  },
  {
    color: "#CFA22D",
    bg: "#FBF3E4",
  },
];

const MAP_COLOR_SCALE = ["#E4F2EE", "#B7DFD4", "#7CC4AF", "#3CA487", "#016B57"];

export default {
  name: "DashboardDashboard",

  data: () => ({
    loading: true,
    loadError: false,

    statCards: [
      {
        title: "Potensi Desa",
        value: "-",
        icon: "mdi-home-city-outline",
        color: "#018C74",
        bg: "#E6F5F1",
      },
      {
        title: "Komoditas",
        value: "-",
        icon: "mdi-leaf",
        color: "#2E9E5B",
        bg: "#E9F7EE",
      },
      {
        title: "Layanan Publik",
        value: "-",
        icon: "mdi-account-group-outline",
        color: "#2F80C4",
        bg: "#E8F2FB",
      },
      {
        title: "Tenants",
        value: "-",
        icon: "mdi-storefront-outline",
        color: "#8E4FD1",
        bg: "#F2EAFB",
      },
    ],

    kategoriPotensi: [],
    potensiSpasial: [],
    activities: [],

    quickStats: [
      {
        label: "Kecamatan",
        value: "-",
        icon: "mdi-map-outline",
        color: "#2F80C4",
        bg: "#E8F2FB",
      },
      {
        label: "Desa / Kelurahan",
        value: "-",
        icon: "mdi-home-city-outline",
        color: "#018C74",
        bg: "#E6F5F1",
      },
      {
        label: "OPD Terdaftar",
        value: "-",
        icon: "mdi-office-building-outline",
        color: "#CFA22D",
        bg: "#FBF3E4",
      },
      {
        label: "Users Aktif",
        value: "-",
        icon: "mdi-account-group-outline",
        color: "#8E4FD1",
        bg: "#F2EAFB",
      },
    ],

    map: null,
    mapLoading: true,
    villageLayer: null,
    allVillageEntries: [],
    legendBuckets: [],
  }),

  computed: {
    adminName() {
      try {
        const data = JSON.parse(localStorage.getItem("userData") || "{}");

        return data.nama_lengkap || data.username || data.nama || "Administrator";
      } catch (e) {
        return "Administrator";
      }
    },

    currentDate() {
      return new Date().toLocaleDateString("id-ID", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    },

    totalPotensi() {
      return this.kategoriPotensi
        .reduce((sum, c) => sum + c.value, 0)
        .toLocaleString("id-ID");
    },

    donutStyle() {
      if (!this.kategoriPotensi.length) {
        return {
          background: "#eef1f0",
        };
      }

      let cumulative = 0;

      const stops = this.kategoriPotensi.map((cat) => {
        const start = cumulative;

        cumulative += cat.percent;

        return `${cat.color} ${start}% ${cumulative}%`;
      });

      return {
        background: `conic-gradient(${stops.join(", ")})`,
      };
    },

    desaCountMap() {
      const map = {};

      this.potensiSpasial.forEach((item) => {
        const nama = this.normalizeText(item.nama_desa_raw || "").toLowerCase();

        if (!nama) return;

        const kec = this.normalizeText(item.nama_kecamatan || "").toLowerCase();

        const key = kec ? `${nama}__${kec}` : nama;

        map[key] = (map[key] || 0) + 1;
      });

      return map;
    },
  },

  mounted() {
    this.fetchDashboardData();

    this.$nextTick(() => {
      this.initDistrictMap();
    });
  },

  beforeDestroy() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },

  watch: {
    desaCountMap() {
      this.refreshDistrictColors();
    },
  },

  methods: {
    async safeGet(path) {
      try {
        const res = await axios.get(`${BASE_URL}${path}`, { headers });

        const body = res.data;

        return body.response ? body.response.data : body.data || [];
      } catch (e) {
        console.error(`Gagal mengambil ${path}:`, e);

        return [];
      }
    },

    formatNumber(n) {
      return Number(n || 0).toLocaleString("id-ID");
    },

    normalizeText(val) {
      return String(val || "")
        .trim()
        .replace(/\s+/g, " ");
    },

    formatRelativeTime(dateStr) {
      if (!dateStr) return "";

      const date = new Date(dateStr);

      if (isNaN(date.getTime())) {
        return "";
      }

      const diffMs = Date.now() - date.getTime();

      const diffMin = Math.round(diffMs / 60000);

      if (diffMin < 1) {
        return "Baru saja";
      }

      if (diffMin < 60) {
        return `${diffMin} menit lalu`;
      }

      const diffHour = Math.round(diffMin / 60);

      if (diffHour < 24) {
        return `${diffHour} jam lalu`;
      }

      const diffDay = Math.round(diffHour / 24);

      if (diffDay < 30) {
        return `${diffDay} hari lalu`;
      }

      return date.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "short",
      });
    },

    async fetchDashboardData() {
      this.loading = true;
      this.loadError = false;

      try {
        const [
          desa,
          komoditas,
          layanan,
          tenants,
          kecamatan,
          opd,
          users,
          potensiSpasial,
        ] = await Promise.all([
          this.safeGet("/desa"),
          this.safeGet("/master/produksi-komoditas"),
          this.safeGet("/layanan"),
          this.safeGet("/tenants/all"),
          this.safeGet("/kecamatan"),
          this.safeGet("/opd"),
          this.safeGet("/master/user"),
          this.safeGet("/master/potensi-spasial"),
        ]);

        this.potensiSpasial = potensiSpasial || [];

        this.statCards = [
          {
            title: "Potensi Desa",
            value: this.formatNumber(potensiSpasial.length),
            icon: "mdi-home-city-outline",
            color: "#018C74",
            bg: "#E6F5F1",
          },

          {
            title: "Komoditas",
            value: this.formatNumber(komoditas.length),
            icon: "mdi-leaf",
            color: "#2E9E5B",
            bg: "#E9F7EE",
          },

          {
            title: "Layanan Publik",
            value: this.formatNumber(layanan.length),
            icon: "mdi-account-group-outline",
            color: "#2F80C4",
            bg: "#E8F2FB",
          },

          {
            title: "Tenants",
            value: this.formatNumber(tenants.length),
            icon: "mdi-storefront-outline",
            color: "#8E4FD1",
            bg: "#F2EAFB",
          },
        ];

        this.quickStats = [
          {
            label: "Kecamatan",
            value: this.formatNumber(kecamatan.length),
            icon: "mdi-map-outline",
            color: "#2F80C4",
            bg: "#E8F2FB",
          },

          {
            label: "Desa / Kelurahan",
            value: 219,
            icon: "mdi-home-city-outline",
            color: "#018C74",
            bg: "#E6F5F1",
          },

          {
            label: "OPD Terdaftar",
            value: this.formatNumber(opd.length),
            icon: "mdi-office-building-outline",
            color: "#CFA22D",
            bg: "#FBF3E4",
          },

          {
            label: "Users Aktif",
            value: this.formatNumber(
              users.filter((u) => u.is_active === undefined || u.is_active == 1).length
            ),
            icon: "mdi-account-group-outline",
            color: "#8E4FD1",
            bg: "#F2EAFB",
          },
        ];

        const counts = {};

        potensiSpasial.forEach((p) => {
          const label = p.nama_kategori || "Lainnya";

          counts[label] = (counts[label] || 0) + 1;
        });

        const total = potensiSpasial.length || 1;

        this.kategoriPotensi = Object.keys(counts)
          .sort((a, b) => counts[b] - counts[a])
          .map((label, i) => ({
            label,
            value: counts[label],
            percent: Math.round((counts[label] / total) * 100),
            color: KATEGORI_PALETTE[i % KATEGORI_PALETTE.length],
          }));

        const recentSources = [
          {
            list: desa,
            key: "nama_desa",
            icon: "mdi-home-city-outline",
            label: "Data potensi desa",
          },

          {
            list: komoditas,
            key:
              "nama_komoditas" in (komoditas[0] || {})
                ? "nama_komoditas"
                : "jenis_komoditas",
            icon: "mdi-leaf",
            label: "Data produksi komoditas",
          },

          {
            list: layanan,
            key: "nama_layanan",
            icon: "mdi-account-group-outline",
            label: "Data layanan publik",
          },

          {
            list: kecamatan,
            key: "nama_kecamatan",
            icon: "mdi-map-marker-radius-outline",
            label: "Data kecamatan",
          },
        ];

        this.activities = recentSources
          .filter((s) => s.list && s.list.length)
          .map((s, i) => {
            const item = s.list[0];

            const palette = ACTIVITY_PALETTE[i % ACTIVITY_PALETTE.length];

            const rawDate = item.updated_at || item.created_at || item.tanggal || null;

            return {
              title: `${s.label}` + (item[s.key] ? ` "${item[s.key]}"` : ""),

              sub: item.updated_by || item.created_by || "Diperbarui oleh sistem",

              time: this.formatRelativeTime(rawDate),

              icon: s.icon,

              color: palette.color,

              bg: palette.bg,
            };
          });

        this.$nextTick(() => {
          this.refreshDistrictColors();
        });
      } catch (e) {
        console.error("Gagal memuat dashboard:", e);

        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },

    async initDistrictMap() {
      this.mapLoading = true;

      this.map = L.map("dashboard-map", {
        zoomControl: false,
        scrollWheelZoom: false,
        dragging: !L.Browser.mobile,
        attributionControl: true,
      }).setView([-7.909473813658169, 113.8981324393563], 9.9);

      // Hilangkan prefix "Leaflet" agar atribusi tetap ringkas
      this.map.attributionControl.setPrefix(false);

      // Tile resmi OpenStreetMap: gratis, tanpa API key
      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "&copy; OpenStreetMap",
        className: "tile-abu", // dipakai untuk filter grayscale di CSS
      }).addTo(this.map);

      await this.loadVillageBoundary();

      this.mapLoading = false;

      this.$nextTick(() => {
        setTimeout(() => {
          if (this.map) {
            this.map.invalidateSize();
          }
        }, 300);
      });
    },

    async loadVillageBoundary() {
      try {
        const response = await fetch("/images/35.11_desa.geojson");

        const geojson = await response.json();

        const entries = [];

        (geojson?.features || []).forEach((feature) => {
          const p = feature.properties || {};

          const namaDesa = this.normalizeText(p.nama_desa || p.NAMOBJ || p.WADMKD || "");

          const namaKec = this.normalizeText(
            p.nm_kecamatan ||
              p.NAMOBJ_1 ||
              p.WADMKC ||
              p.kecamatan ||
              p.nama_kecamatan ||
              ""
          );

          if (!namaDesa) return;

          entries.push({
            nama: namaDesa,
            kecamatan: namaKec,
          });
        });

        this.allVillageEntries = entries;

        this.villageLayer = L.geoJSON(geojson, {
          style: (feature) => this.villageStyle(feature),

          onEachFeature: (feature, layer) => {
            const nama = this.villageFeatureName(feature);

            const kecamatan = this.villageFeatureKecamatan(feature);

            const count = this.villageCount(nama, kecamatan);

            layer.bindTooltip(
              `<b>${nama}</b>${
                kecamatan
                  ? ` <span style="font-weight:400">(Kec. ${kecamatan})</span>`
                  : ""
              }<br/>${count} potensi`,
              {
                sticky: true,
                direction: "top",
                className: "label-desa-count",
              }
            );

            layer.on({
              mouseover: (e) => {
                e.target.setStyle({
                  weight: 2,
                  fillOpacity: 0.9,
                });
              },

              mouseout: (e) => {
                if (this.villageLayer) {
                  this.villageLayer.resetStyle(e.target);
                }
              },
            });
          },
        }).addTo(this.map);

        this.buildLegend();
      } catch (e) {
        console.error("Gagal memuat batas desa", e);
      }
    },

    villageFeatureName(feature) {
      const p = feature.properties || {};

      return this.normalizeText(p.nama_desa || p.NAMOBJ || p.WADMKD || "Desa");
    },

    villageFeatureKecamatan(feature) {
      const p = feature.properties || {};

      return this.normalizeText(
        p.nm_kecamatan || p.NAMOBJ_1 || p.WADMKC || p.kecamatan || p.nama_kecamatan || ""
      );
    },

    villageCount(nama, kecamatan) {
      const n = nama.toLowerCase();

      const k = (kecamatan || "").toLowerCase();

      const key = k ? `${n}__${k}` : n;

      return this.desaCountMap[key] || 0;
    },

    mapColorBuckets() {
      const counts = Object.values(this.desaCountMap);

      const max = Math.max(1, ...counts);

      return {
        max,

        t1: Math.round(max * 0.15),

        t2: Math.round(max * 0.35),

        t3: Math.round(max * 0.6),

        t4: Math.round(max * 0.85),
      };
    },

    bucketColor(count) {
      const { t1, t2, t3, t4 } = this.mapColorBuckets();

      if (!count) {
        return "#EDEFF1";
      }

      if (count > t4) {
        return MAP_COLOR_SCALE[4];
      }

      if (count > t3) {
        return MAP_COLOR_SCALE[3];
      }

      if (count > t2) {
        return MAP_COLOR_SCALE[2];
      }

      if (count > t1) {
        return MAP_COLOR_SCALE[1];
      }

      return MAP_COLOR_SCALE[0];
    },

    villageStyle(feature) {
      const nama = this.villageFeatureName(feature);

      const kecamatan = this.villageFeatureKecamatan(feature);

      const count = this.villageCount(nama, kecamatan);

      return {
        color: "#ffffff",
        weight: 1,
        opacity: 1,
        fillColor: this.bucketColor(count),
        fillOpacity: 0.82,
      };
    },

    buildLegend() {
      const { t1, t2, t3, t4, max } = this.mapColorBuckets();

      this.legendBuckets = [
        {
          label: `> ${t4}`,
          color: MAP_COLOR_SCALE[4],
        },

        {
          label: `${t2 + 1} - ${t3}`,
          color: MAP_COLOR_SCALE[2],
        },

        {
          label: `1 - ${t1}`,
          color: MAP_COLOR_SCALE[0],
        },

        {
          label: "Tidak ada data",
          color: "#EDEFF1",
        },
      ];

      if (!max) {
        this.legendBuckets = [
          {
            label: "Belum ada data",
            color: "#EDEFF1",
          },
        ];
      }
    },

    refreshDistrictColors() {
      if (!this.villageLayer) {
        return;
      }

      this.villageLayer.eachLayer((layer) => {
        const nama = this.villageFeatureName(layer.feature);

        const kecamatan = this.villageFeatureKecamatan(layer.feature);

        const count = this.villageCount(nama, kecamatan);

        layer.setStyle(this.villageStyle(layer.feature));

        if (layer.getTooltip()) {
          layer.setTooltipContent(
            `<b>${nama}</b>${
              kecamatan ? ` <span style="font-weight:400">(Kec. ${kecamatan})</span>` : ""
            }<br/>${count} potensi`
          );
        }
      });

      this.buildLegend();
    },
  },
};
</script>

<style scoped>
.dashboard-wrapper {
  min-height: 100vh;
  padding: 22px 24px 40px !important;
  background: #f6f8f7;
  color: #172522;
}

.welcome-section {
  position: relative;
  min-height: 210px;
  overflow: hidden;
  border-radius: 24px;
  margin-bottom: 28px;
  background: linear-gradient(110deg, #063e34 0%, #08735e 60%, #07947a 100%);
  box-shadow: 0 16px 40px rgba(1, 75, 62, 0.13);
}

.welcome-bg {
  position: absolute;
  inset: 0;

  background: radial-gradient(
      circle at 75% 20%,
      rgba(255, 255, 255, 0.12),
      transparent 28%
    ),
    radial-gradient(circle at 90% 100%, rgba(255, 255, 255, 0.08), transparent 32%);
}

.welcome-content {
  position: relative;
  z-index: 2;

  min-height: 210px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 30px 42px;
}

.welcome-left {
  max-width: 570px;
  z-index: 3;
}

.welcome-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;

  padding: 6px 11px;

  border-radius: 30px;

  background: rgba(255, 255, 255, 0.1);

  border: 1px solid rgba(255, 255, 255, 0.16);

  color: #b7f2e4;

  font-size: 11px;
  font-weight: 700;

  margin-bottom: 14px;
}

.pulse-dot {
  width: 7px;
  height: 7px;

  border-radius: 50%;

  background: #53e0bd;

  box-shadow: 0 0 0 4px rgba(83, 224, 189, 0.12);
}

.welcome-left h1 {
  margin: 0;

  color: white;

  font-size: 27px;
  line-height: 1.2;
  font-weight: 400;

  letter-spacing: -0.5px;
}

.welcome-left h1 strong {
  font-weight: 800;
}

.welcome-left p {
  max-width: 480px;

  margin: 10px 0 17px;

  color: rgba(255, 255, 255, 0.72);

  font-size: 13px;
  line-height: 1.6;
}

.welcome-meta {
  display: flex;
  align-items: center;
  gap: 13px;

  color: rgba(255, 255, 255, 0.72);

  font-size: 11px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 5px;
}

.meta-separator {
  width: 4px;
  height: 4px;

  border-radius: 50%;

  background: rgba(255, 255, 255, 0.3);
}

.welcome-visual {
  position: absolute;

  right: 0;
  top: 0;

  width: 48%;
  height: 100%;

  display: flex;
  justify-content: flex-end;
  align-items: center;

  pointer-events: none;
}

.welcome-visual img {
  width: 100%;
  height: 100%;

  object-fit: cover;

  object-position: right center;

  opacity: 0.82;

  mix-blend-mode: screen;
}

.section-heading,
.regional-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  margin-bottom: 15px;
}

.eyebrow {
  display: block;

  color: #018c74;

  font-size: 9px;
  font-weight: 800;

  letter-spacing: 1.5px;

  margin-bottom: 3px;
}

.section-heading h2,
.regional-header h2 {
  margin: 0;

  font-size: 18px;
  font-weight: 800;

  color: #182824;
}

.last-update {
  display: flex;
  align-items: center;
  gap: 6px;

  font-size: 11px;
  color: #84918e;
}

.update-dot {
  width: 6px;
  height: 6px;

  border-radius: 50%;

  background: #1fa97e;
}

.overview-section {
  margin-bottom: 28px;
}

.overview-grid {
  display: grid;

  grid-template-columns: repeat(4, 1fr);

  background: white;

  border: 1px solid #e8edeb;

  border-radius: 18px;

  overflow: hidden;
}

.overview-item {
  min-height: 105px;

  display: flex;
  align-items: center;

  padding: 20px;

  position: relative;

  border-right: 1px solid #edf1ef;

  transition: background 0.2s ease;
}

.overview-item:last-child {
  border-right: none;
}

.overview-item:hover {
  background: #fbfdfc;
}

.overview-icon {
  width: 45px;
  height: 45px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 13px;

  margin-right: 13px;
}

.overview-content {
  min-width: 0;
}

.overview-label {
  color: #7d8986;

  font-size: 11px;

  margin-bottom: 3px;
}

.overview-value {
  color: #182824;

  font-size: 21px;
  font-weight: 800;
}

.overview-arrow {
  position: absolute;

  right: 17px;
  top: 16px;

  color: #c1cbc8;
}

.content-section {
  display: grid;

  grid-template-columns:
    minmax(0, 1.45fr)
    minmax(280px, 0.85fr)
    minmax(280px, 0.85fr);

  gap: 18px;

  align-items: stretch;

  margin-bottom: 28px;
}

.main-panel {
  min-width: 0;

  background: white;

  border: 1px solid #e8edeb;

  border-radius: 20px;

  padding: 22px;

  box-shadow: 0 5px 20px rgba(25, 55, 47, 0.025);
}

.panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  margin-bottom: 17px;
}

.panel-header h3 {
  margin: 0;

  color: #182824;

  font-size: 16px;
  font-weight: 800;
}

.panel-header p {
  margin: 4px 0 0;

  color: #8b9794;

  font-size: 10.5px;
}

.panel-action {
  color: #018c74 !important;

  text-transform: none;

  font-size: 11px !important;

  font-weight: 700;

  min-width: 0;
  padding: 0 !important;
}

.map-panel {
  display: flex;
  flex-direction: column;
}

.map-container {
  position: relative;

  height: 335px;

  overflow: hidden;

  border-radius: 15px;

  background: #edf2f0;
}

#dashboard-map {
  position: absolute;
  inset: 0;

  z-index: 1;

  background: #edf2f0;
}

.map-loading {
  position: absolute;
  inset: 0;

  z-index: 8;

  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;

  gap: 9px;

  background: rgba(255, 255, 255, 0.72);

  color: #71807b;

  font-size: 11px;
}

.map-floating-info {
  position: absolute;

  z-index: 5;

  left: 13px;
  top: 13px;

  padding: 10px 13px;

  min-width: 125px;

  border-radius: 11px;

  background: rgba(255, 255, 255, 0.94);

  box-shadow: 0 5px 18px rgba(0, 0, 0, 0.1);

  backdrop-filter: blur(8px);
}

.map-info-title {
  color: #82908c;

  font-size: 9px;
}

.map-info-value {
  color: #172824;

  font-size: 18px;
  font-weight: 800;

  line-height: 1.2;
}

.map-info-sub {
  color: #9ca7a4;

  font-size: 8px;
}

.map-legend {
  position: absolute;

  z-index: 5;

  right: 12px;
  bottom: 12px;

  padding: 9px 11px;

  border-radius: 10px;

  background: rgba(255, 255, 255, 0.94);

  box-shadow: 0 5px 18px rgba(0, 0, 0, 0.1);

  font-size: 9px;
}

.legend-title {
  color: #596662;

  font-size: 9px;
  font-weight: 800;

  margin-bottom: 6px;
}

.legend-item {
  display: flex;
  align-items: center;

  gap: 6px;

  color: #74807d;

  margin-top: 4px;
}

.legend-color {
  width: 12px;
  height: 8px;

  border-radius: 2px;
}

.map-footer {
  display: flex;
  align-items: center;

  gap: 17px;

  padding-top: 14px;
}

.map-footer-item {
  display: flex;
  align-items: center;
  gap: 5px;

  color: #899590;

  font-size: 10px;
}

.map-footer-item .v-icon {
  color: #7f8d88 !important;
}

.map-detail-btn {
  margin-left: auto;

  background: #eaf6f3 !important;

  color: #018c74 !important;

  text-transform: none;

  font-size: 10px !important;
  font-weight: 700;

  border-radius: 9px !important;

  box-shadow: none !important;
}

.category-summary {
  display: flex;
  align-items: center;

  padding: 7px 0 20px;

  border-bottom: 1px solid #edf1ef;
}

.category-donut {
  width: 126px;
  height: 126px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;
}

.category-donut-inner {
  width: 78px;
  height: 78px;

  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;

  border-radius: 50%;

  background: white;

  box-shadow: 0 0 0 1px #f1f3f2;
}

.category-donut-inner strong {
  color: #1b2925;

  font-size: 17px;
  font-weight: 800;
}

.category-donut-inner span {
  color: #9aa6a3;

  font-size: 8px;
}

.category-summary-text {
  display: flex;
  flex-direction: column;

  margin-left: 18px;
}

.category-summary-text strong {
  color: #1a2925;

  font-size: 20px;
}

.category-summary-text span {
  color: #66746f;

  font-size: 10px;
}

.category-summary-text small {
  color: #a0aaa7;

  font-size: 8px;

  margin-top: 3px;
}

.category-list {
  padding-top: 8px;
}

.category-row {
  display: grid;

  grid-template-columns:
    minmax(0, 1fr)
    auto
    38px;

  align-items: center;

  gap: 8px;

  padding: 8px 0;

  border-bottom: 1px solid #f1f3f2;
}

.category-row:last-child {
  border-bottom: none;
}

.category-name {
  display: flex;
  align-items: center;

  min-width: 0;

  color: #56635f;

  font-size: 10px;
}

.category-name > span:last-child {
  overflow: hidden;

  white-space: nowrap;

  text-overflow: ellipsis;
}

.category-dot {
  width: 7px;
  height: 7px;

  flex-shrink: 0;

  border-radius: 50%;

  margin-right: 7px;
}

.category-number {
  color: #23312d;

  font-size: 10px;
  font-weight: 800;
}

.category-percent {
  color: #9aa5a2;

  font-size: 9px;

  text-align: right;
}

.activity-live {
  display: flex;
  align-items: center;

  gap: 5px;

  padding: 4px 7px;

  border-radius: 6px;

  background: #eef8f5;

  color: #018c74;

  font-size: 8px;
  font-weight: 800;
}

.activity-live span {
  width: 5px;
  height: 5px;

  border-radius: 50%;

  background: #1fa97e;
}

.activity-list {
  display: flex;
  flex-direction: column;

  gap: 3px;
}

.activity-row {
  display: flex;
  align-items: center;

  gap: 10px;

  padding: 10px 0;

  border-bottom: 1px solid #f0f3f1;
}

.activity-row:last-child {
  border-bottom: none;
}

.activity-avatar {
  width: 34px;
  height: 34px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 10px;
}

.activity-content {
  flex: 1;
  min-width: 0;
}

.activity-title {
  color: #25332f;

  font-size: 9.5px;
  font-weight: 700;

  line-height: 1.35;

  overflow: hidden;

  text-overflow: ellipsis;

  white-space: nowrap;
}

.activity-sub {
  color: #9aa6a3;

  font-size: 8px;

  margin-top: 2px;

  overflow: hidden;

  text-overflow: ellipsis;

  white-space: nowrap;
}

.activity-time {
  color: #a2ada9;

  font-size: 8px;

  white-space: nowrap;
}

.regional-section {
  background: white;

  border: 1px solid #e8edeb;

  border-radius: 20px;

  padding: 22px;

  box-shadow: 0 5px 20px rgba(25, 55, 47, 0.025);
}

.regional-description {
  color: #9aa6a3;

  font-size: 10px;
}

.regional-grid {
  display: grid;

  grid-template-columns: repeat(4, 1fr);

  border-top: 1px solid #edf1ef;

  border-bottom: 1px solid #edf1ef;
}

.regional-item {
  min-height: 82px;

  display: flex;
  align-items: center;

  position: relative;

  padding: 13px 17px;

  border-right: 1px solid #edf1ef;
}

.regional-item:last-child {
  border-right: none;
}

.regional-icon {
  width: 39px;
  height: 39px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 11px;

  margin-right: 11px;
}

.regional-data {
  display: flex;
  flex-direction: column;
}

.regional-data span {
  color: #929e9a;

  font-size: 9px;

  margin-bottom: 2px;
}

.regional-data strong {
  color: #1b2925;

  font-size: 17px;
  font-weight: 800;
}

.regional-arrow {
  position: absolute;

  right: 13px;
  top: 13px;

  color: #ccd3d1;
}

.panel-loading {
  min-height: 240px;

  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-state {
  min-height: 240px;

  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;

  gap: 9px;

  color: #a0aaa7;

  font-size: 10px;
}

.empty-state .v-icon {
  color: #c7d0cd !important;
}

::v-deep .leaflet-container {
  font-family: "Roboto", sans-serif;
}

/* Tile OpenStreetMap dibuat abu-abu terang agar mirip basemap light */
::v-deep .tile-abu {
  filter: grayscale(1) brightness(1.08) contrast(0.9);
}

::v-deep .leaflet-control-attribution {
  font-size: 8px !important;
  background: rgba(255, 255, 255, 0.7) !important;
  padding: 1px 5px !important;
  border-radius: 6px 0 0 0;
}

::v-deep .label-desa-count {
  background: rgba(255, 255, 255, 0.95) !important;

  border: none !important;

  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.13) !important;

  color: #16221e !important;

  font-size: 10px !important;

  padding: 4px 8px !important;

  border-radius: 7px !important;
}

::v-deep .label-desa-count:before {
  border-top-color: rgba(255, 255, 255, 0.95) !important;
}

@media (max-width: 1200px) {
  .content-section {
    grid-template-columns:
      1.2fr
      1fr;
  }

  .map-panel {
    grid-column: 1 / -1;
  }

  .map-container {
    height: 350px;
  }
}

@media (max-width: 900px) {
  .overview-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .overview-item:nth-child(2) {
    border-right: none;
  }

  .overview-item:nth-child(-n + 2) {
    border-bottom: 1px solid #edf1ef;
  }

  .content-section {
    grid-template-columns: 1fr;
  }

  .map-panel {
    grid-column: auto;
  }

  .regional-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .regional-item:nth-child(2) {
    border-right: none;
  }

  .regional-item:nth-child(-n + 2) {
    border-bottom: 1px solid #edf1ef;
  }
}

@media (max-width: 600px) {
  .dashboard-wrapper {
    padding: 12px 10px 25px !important;
  }

  .welcome-section {
    min-height: 230px;

    border-radius: 18px;
  }

  .welcome-content {
    min-height: 230px;

    padding: 23px 20px;

    align-items: flex-start;
  }

  .welcome-left h1 {
    font-size: 23px;
  }

  .welcome-left p {
    font-size: 11px;

    max-width: 270px;
  }

  .welcome-meta {
    flex-wrap: wrap;

    font-size: 9px;
  }

  .welcome-visual {
    width: 100%;

    opacity: 0.35;
  }

  .overview-grid {
    grid-template-columns: 1fr;
  }

  .overview-item,
  .overview-item:nth-child(2) {
    border-right: none;

    border-bottom: 1px solid #edf1ef;
  }

  .overview-item:last-child {
    border-bottom: none;
  }

  .section-heading,
  .regional-header {
    align-items: flex-start;

    flex-direction: column;

    gap: 6px;
  }

  .content-section {
    gap: 12px;
  }

  .main-panel,
  .regional-section {
    padding: 17px;

    border-radius: 17px;
  }

  .map-container {
    height: 280px;
  }

  .map-footer {
    flex-wrap: wrap;
  }

  .map-detail-btn {
    width: 100%;

    margin-left: 0;
  }

  .category-summary {
    justify-content: center;
  }

  .regional-grid {
    grid-template-columns: 1fr;
  }

  .regional-item,
  .regional-item:nth-child(2) {
    border-right: none;

    border-bottom: 1px solid #edf1ef;
  }

  .regional-item:last-child {
    border-bottom: none;
  }

  .regional-description {
    display: none;
  }
}
</style>
